import { refreshTokenAPI, requestApiGet, requestApiPostTwo, setAuthTokenInHeader } from '../api';
import { dispatchToken, logout, useTokenStore } from '../storage/tokenState';
import { isEmpty } from "lodash/";
import { AUTHENTICATE_USER, REFRESH_TOKEN, USER_DETAILS } from '../appConstants/APIEndpoint';
import { ELanguage, IUserDetails, PreSignUpConfig } from '../storage/store';
import { dispatchPostSignupConfig, dispatchPreSignupConfig, dispatchSignUpData } from '../storage/configState';
import { type AuthApiOne } from '../interfaces/AuthApi';
import { MAIN_CLIENT, SIGN_IN_ACTION, GOOGLE_LOGIN_KEY } from '../appConstants/AppConstant';
import { dispatchUserDetails } from '../storage/userState';

import { _isEmpty } from '../utils/referralLodash';

import { SIGN_IN_CODES } from '../constant';
import { getDeviceId } from '../api/commonApi';
import { OnlyPostSignUpConfig } from '../interfaces/PrivateConfig';
import { splitSwitchesFromPostSignUp, splitSwitchesFromPreSignUp } from '../utils/beToFeTransformer';
import { dispatchSwitchData } from '../storage/switchState';
import { getNetworkInfo } from '../utils/NetInfo';
import { getDeviceSpec, getFormattedTimestamp, getOSVersion } from '../components/common';
//import { setAuthApiResponseStatus } from '../store/actions/actions';
import { LANGUAGE } from '../constant';

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          initialize: (config: any) => void;
          prompt: (callback: (notification: any) => void) => void;
        };
        oauth2?: {
          initTokenClient: (config: any) => any;
        };
      };
    };
  }
}

declare const google: any;

let isRefreshingToken = false;

export const getAuthApiRequestParams = (
  phone: string,
  action: string,
  language: string,
  isNewAuth: boolean, // true for new signup, false for login
  otp?: string,
  accessToken?: string,
  refreshToken?: string,
  _id?: string,
  zupeeUserId?: number,
  refreshTokenExpiresAt?: number,
  partnerId?: string,
  screenName?: string,
  tokenForLoginSkip?: string,
  isReferralSuperstar?: boolean
) => {
  const deviceID = getDeviceId();
  const networkInfo = getNetworkInfo();
  const { isConnected, effectiveType } = networkInfo;
  const osVersion = getOSVersion();
  const deviceSpec = getDeviceSpec();
  const formattedTimestamp = getFormattedTimestamp();
  // const deviceID = DeviceInfo.getUniqueId();

  // const appVersion = Platform.OS === 'android' ? '3000' : '656';

  // Common parameters for both signup and login
  const baseParams = {
    socketType: MAIN_CLIENT,
    merchantName: "SUPER_ZUPEE",
    DeviceId: deviceID,
    det: 'android',
    signupPhoneNumber: phone,
    gaId: deviceID,
    ult: 'phone',
    lc: localStorage.getItem("language") || LANGUAGE,
    languagePreference: localStorage.getItem("language") || LANGUAGE,
    isNewSignupFlowEnabled: true,
    av: `${process.env.REACT_APP_VERSION}`,
    version_code: `${process.env.REACT_APP_VERSION}`,
    app_version: `${process.env.REACT_APP_APPVERSION}`,
    anov: osVersion,
    rfc: '',
    uniqueDeviceId: deviceID,
    packageName: `${process.env.REACT_APP_APP_NAME}`,
    SerialNumber: deviceID,
    /*pwaAuthData: {
      partnerId: partnerId,
      userAuthenticatedScreen: screenName || ''
   },*/
   connectionInfo: {
    isConnected: isConnected,
    networkType: effectiveType
  },
  deviceType: "android",
  specs: deviceSpec,
  // isUsbDebuggingEnabled: true,
  timeStamp: formattedTimestamp,
  "user-property": "00000",
  tokenForLoginSkip: tokenForLoginSkip,
  isReferralSuperstar: isReferralSuperstar
  };

  if (isNewAuth) {
    // Simplified request - only required fields for authenticateUser API
    // Sending only: ult, newauth, signupPhoneNumber, otp, action, languagePreference, merchantName
    // This matches the API requirement: https://cse-stv-superapp-service.ludojoy.com/v1.0/auth/authenticateUser
    return {
      ult: 'phone',
      newauth: true,
      signupPhoneNumber: phone,
      otp: otp,
      action: action,
      languagePreference: localStorage.getItem("language") || LANGUAGE || 'en',
      merchantName: "SUPER_ZUPEE",
      av: "1010",
      deviceType: "web",
      det : "web",
      user_login_type: 'web',
      uniqueDeviceId: deviceID,
      gaId: deviceID,
      socketType: MAIN_CLIENT,
      packageName: 'com.eight.network.android.vertical',
    };
  } else {
    return {
      ...baseParams,
      reauth: true,
      lastAccessToken: accessToken,
      refreshToken: refreshToken || '',
      uid: _id,
      zupeeUserId: zupeeUserId,
      refreshTokenExpiresAt,
    };
  }
};

export const authenticateUserApi = async (
  request: ReturnType<typeof getAuthApiRequestParams>,
  dispatch?: any
) => {
  try {
    // Simplified - only send required fields, no need to copy request
    const responseData = await requestApiPostTwo<AuthApiOne>(
      AUTHENTICATE_USER,
      request
    );
    if (responseData?.response?.success) {
      const authResponse = responseData.response;
      if (authResponse?.flag === false && authResponse?.errorCode !== SIGN_IN_CODES.SUCCESS_CODE) {
        if (authResponse?.errorCode === SIGN_IN_CODES.INCORRECT_OTP) {
          console.log('Silent Login failed due to incorrect OTP', { isAddEvent: true });
          return responseData.response;
        }
        return responseData.response;
      }
      // Commented out saveAuthApiResponse - keeping minimal, only essential token storage in OtpScreen
      // saveAuthApiResponse(authResponse, lastAccessToken);
      return responseData.response;
    } else {
      console.log("Auth API failed");
    }
  } catch (error) {
    console.error("Network or Logic Error:", error);
  }
};

export const silentLoginByAuthAPI = async (dispatch?: any) => {
  const token = useTokenStore.getState().token;
  // logger(TAG_NAMES.NAVIGATOR, 'Silent Login Triggered');
  if (!token) {
    // logger(TAG_NAMES.API_LOGS, 'Unable to login user since no token');
    return;
  }
  const {
    accessToken,
    refreshToken,
    _id,
    zupeeUserId,
    signupPhoneNumber,
    refreshTokenExpiresAt,
    language=localStorage.getItem('language') || LANGUAGE,
  } = token;
  if (!signupPhoneNumber) {
    // logger(
    //   TAG_NAMES.API_LOGS,
    //   'Unable to login user since no signupPhoneNumber',
    // );
    return;
  }
  const requestParam = getAuthApiRequestParams(
    signupPhoneNumber,
    SIGN_IN_ACTION,
    language,
    false,
    undefined,
    accessToken,
    refreshToken,
    _id,
    zupeeUserId,
    refreshTokenExpiresAt,
  );
  authenticateUserApi(requestParam, dispatch).then(async authResponse => {
    authErrorHandler(authResponse)

    await fetchAndStoreUserDetails(language as ELanguage);
    if(authResponse?.rejoinInfo?.rejoin === 1){
      if (!_isEmpty(authResponse?.rejoinInfo?.gameplayServer)) {
         console.log("silent login success comment gameplay in authService.ts file");
        // console.log(" gameplayServergameplayServer", gameplayServer)
            // initializeGameplaySocket(authResponse?.rejoinInfo?.gameplayServer, {
            //   en: RJT,
            //   data: { ltid: "000" },
            // });
      }
    }
  }).catch(error => {
    logout();
    console.log('Silent Login Failed', {
      isAddEvent: true,
      error,
    });
  });;
};

export const saveAuthApiResponse = (authResponse: any, lastAccessToken?: string) => {
  const signUpData = authResponse?.signUpData;
  const postConfig = authResponse?.postConfig;
  const token = useTokenStore.getState().token;
  const {
    pp,
    ue,
    un,
    ult,
    signupPhoneNumber,
    _id,
    accessToken,
    zupeeUserId,
    refreshToken,
    refreshTokenExpiresAt,
    validity,
    isNewSignup,
    rummyAccessToken,
  } = signUpData || {};

  const dataToSave = {
    pp,
    un,
    ue,
    ult,
    accessToken,
    refreshToken: refreshToken ? refreshToken : token.refreshToken,
    signupPhoneNumber,
    _id,
    zupeeUserId,
    refreshTokenExpiresAt,
    validity: Date.now() + validity - 10, //We're adding 10 seconds to avoid refreshing token at the last second
    isNewSignup,
    rummyAccessToken,
    lastAccessToken: lastAccessToken || "",
  };
  dispatchToken(dataToSave);

  if (accessToken && _id) {
    accessToken && _id && setAuthTokenInHeader(accessToken, _id);
    // logger(TAG_NAMES.API_LOGS, 'Token Saved', { data: { accessToken, _id } });
  }

  if (postConfig) {
    // logger(TAG_NAMES.API_LOGS, 'Post Signup saved');
    const { _otherData, _switchPostSignUpData } = splitSwitchesFromPostSignUp(postConfig);
    dispatchPostSignupConfig(_otherData as OnlyPostSignUpConfig);
    dispatchSwitchData(_switchPostSignUpData);
  }

  if (signUpData) {
    // logger(TAG_NAMES.API_LOGS, 'Signup data saved');
    dispatchSignUpData(signUpData);
  }

  // fetchOnboardingConfig(
  //   signUpData?.onboardingCompleted,
  //   authResponse?.rejoinInfo?.rejoin || 0,
  // ); //Call once Header is set

  //TODO Test this API with ScreenName useGetNudgesByScreenNameAPI('LOBBY');
};

export const fetchAndStoreUserDetails = async (language: ELanguage) => {
  const userResp = await requestApiGet<IUserDetails>(USER_DETAILS);
  if (isEmpty(userResp.response.userDetail)) { return; }

  dispatchUserDetails(userResp.response.userDetail, language);
};

export const refreshTokenDuplicate = async () => {
  try {
    const token = useTokenStore.getState().token;
    const { ult, accessToken, refreshToken, refreshTokenExpiresAt = 0 } = token || {};
    console.log('refreshTokenDuplicate', token);
    if (!isEmpty(refreshToken) && refreshTokenExpiresAt > Date.now()) {

      // const serialNumber = await getUniqueDeviceId();
      const data = {
        // deviceType: Platform.OS,
        // deviceId: serialNumber,
        loginType: ult,
        refreshToken,
        accessToken,
      };

      console.log('refreshTokenDuplicate API called');
      const res = await refreshTokenAPI(REFRESH_TOKEN, data);
      const { accessToken: newAccessToken, success, validity } = res.response;
      console.log('refreshTokenDuplicate API response', res);
      if (success) {

        const newToken = { ...token, accessToken: newAccessToken, validity: Date.now() + validity - 10 };
        dispatchToken(newToken);
        setAuthTokenInHeader(newAccessToken);

        return Promise.resolve(accessToken);
      } else {
        logout();
        return Promise.reject();
      }
    } else {
      logout();
      return Promise.reject();
    }
  } catch (error: any) {
    logout();
    return Promise.reject(error);
  }
};

export const isTokenValid = () => {
  const token = useTokenStore.getState()?.token;
  if (isEmpty(token)) {
    return true;
  }
  const accessTokenValidity = token?.validity || 0;
  console.log(`About to refresh after, ${accessTokenValidity - Date.now()}`);

  return !isEmpty(token?.accessToken) && accessTokenValidity > Date.now();
};

export const onPreSignupSuccess = (preSignupConfig: PreSignUpConfig) => {
  if (!isEmpty(preSignupConfig)) {
    const { _otherData, _switchPreSignUpData } = splitSwitchesFromPreSignUp(preSignupConfig);
    dispatchPreSignupConfig(_otherData);
    dispatchSwitchData(_switchPreSignUpData);
  }
};

const authErrorHandler =(authResponse: ({ success: boolean; error?: string; } & Partial<AuthApiOne>) | undefined)=>{
  if (authResponse?.flag === false && authResponse?.errorCode === SIGN_IN_CODES.INVALID_REFRESH_TOKEN) {
    console.log('Silent Login failed due to invalid refresh token');
    logout();
    return;
  }
  if (authResponse?.flag === false && authResponse?.errorCode !== SIGN_IN_CODES.SUCCESS_CODE) {
    if (authResponse?.errorCode === SIGN_IN_CODES.INCORRECT_OTP) {
      console.log('Silent Login failed due to incorrect OTP', { isAddEvent: true });
      // logout();
      return;
    }
    console.log('Silent Login failed due to some error', {
      data: { errorCode: authResponse?.errorCode },
    });
    logout();
    return;
  }
}

/**
 * Silently refreshes the Google token before it expires
 * @returns Promise<string> - Returns the fresh token
 */
export const refreshGoogleToken = async (forceRefresh = false): Promise<string> => {
  try {
    // Prevent concurrent refresh attempts
    if (isRefreshingToken) {
      console.log('Token refresh already in progress, skipping...');
      return Promise.reject('Token refresh already in progress');
    }

    const token = sessionStorage.getItem(GOOGLE_LOGIN_KEY);
    if (!token) {
      return Promise.reject('No Google token found');
    }

    // Decode the existing token to check expiration
    const decodedToken = JSON.parse(atob(token.split('.')[1]));
    const expirationTime = decodedToken.exp * 1000;
    const currentTime = Date.now();
    
    console.log('Token status:', {
      currentTime: new Date(currentTime).toLocaleString(),
      expirationTime: new Date(expirationTime).toLocaleString(),
      timeRemaining: Math.floor((expirationTime - currentTime) / 1000 / 60) + ' minutes'
    });

    // Skip expiration check if forceRefresh is true
    if (!forceRefresh && expirationTime - currentTime > 5 * 60 * 1000) {
      console.log('Token still valid, no refresh needed');
      return token;
    }

    console.log(forceRefresh ? 'Forcing token refresh...' : 'Attempting to refresh token...');
    isRefreshingToken = true;

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        isRefreshingToken = false;
        reject('Token refresh timeout');
      }, 30000); // 30 second timeout

      google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
        callback: (response: any) => {
          clearTimeout(timeoutId);
          isRefreshingToken = false;
          if (response.credential) {
            console.log('New JWT token received:', response);
            sessionStorage.setItem(GOOGLE_LOGIN_KEY, response.credential);
            resolve(response.credential);
          } else {
            console.log('Failed to get JWT token:', response);
            reject('Failed to get JWT token');
          }
        },
        prompt_parent_id: "google_signin_prompt",
        auto_select: true
      });

      try {
        google.accounts.id.prompt((notification: any) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            clearTimeout(timeoutId);
            isRefreshingToken = false;
            console.log('Google prompt not displayed or skipped. Falling back to existing token');
            resolve(token); // Fall back to existing token instead of rejecting
          }
        });
      } catch (error) {
        clearTimeout(timeoutId);
        isRefreshingToken = false;
        reject(error);
      }
    });
  } catch (error) {
    isRefreshingToken = false;
    console.error('Error refreshing Google token:', error);
    return Promise.reject(error);
  }
};

// Helper function to set up periodic token refresh
export const setupGoogleTokenRefresh = (testMode = false) => {
  const refreshInterval = testMode ? 5 * 60 * 1000 : 55 * 60 * 1000;
  console.log(`Setting up token refresh every ${refreshInterval/1000/60} minutes`);
  
  // Initial check with force refresh
  refreshGoogleToken(testMode)
    .then(() => console.log('Initial token check completed'))
    .catch(error => console.error('Initial token check failed:', error));

  return setInterval(async () => {
    console.log('Running scheduled token refresh...');
    try {
      const newToken = await refreshGoogleToken(testMode);
      console.log('Token refresh successful:', newToken.substring(0, 10) + '...');
    } catch (error) {
      console.error('Failed to refresh Google token:', error);
    }
  }, refreshInterval);
};